import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Icon } from "../icon";
import { Tooltip } from "../tooltip";

export type TableSize = "sm" | "md" | "lg";
export type TableAlign = "left" | "center" | "right";
export type TableContainerBreakpoint =
  | "compact"
  | "comfortable"
  | "expanded"
  | "wide";
export type TableSortDirection = "ascending" | "descending" | false;
export type TableRowAccentTone =
  | "brand"
  | "positive"
  | "warning"
  | "negative"
  | "informative";
export type TableCellVariant =
  | "starting"
  | "text"
  | "doubleLine"
  | "chip"
  | "chipText"
  | "currency"
  | "icon"
  | "button"
  | "toggle"
  | "dropdown"
  | "input"
  | "calendar"
  | "expand"
  | "endingSpacer";

const DEFAULT_WIDTH_BY_VARIANT: Record<TableCellVariant, string> = {
  starting: "169px",
  text: "170px",
  doubleLine: "250px",
  chip: "90px",
  chipText: "250px",
  currency: "130px",
  icon: "52px",
  button: "191px",
  toggle: "207px",
  dropdown: "182px",
  input: "var(--table-input-cell-width)",
  calendar: "208px",
  expand: "80px",
  endingSpacer: "8px",
};

const tableVariants = cva(
  [
    "w-full",
    "caption-bottom",
    "border-collapse",
    "bg-table-row-light-surface",
    "text-text-default",
    "font-(family-name:--font-family-body)",
    "text-(length:--font-size-body-medium)",
    "leading-(--font-line-height-20)",
    "@max-[480px]/data-table:[--table-input-cell-width:167px]",
    "@max-[480px]/data-table:[--table-row-height:56px]",
  ],
  {
    variants: {
      size: {
        sm: ["[--table-input-cell-width:167px]", "[--table-row-height:56px]"],
        md: ["[--table-input-cell-width:167px]", "[--table-row-height:68px]"],
        lg: ["[--table-input-cell-width:208px]", "[--table-row-height:84px]"],
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const rowVariants = cva(
  [
    "relative",
    "border-b",
    "border-table-outline",
    "transition-[box-shadow]",
    "duration-200",
    "ease-in-out",
    "data-[interactive]:cursor-pointer",
    "data-[selected]:bg-table-row-select",
  ],
  {
    variants: {
      section: {
        header: ["h-(--spacing-40)"],
        body: [
          "h-(--table-row-height)",
          "hover:z-[1]",
          "hover:shadow-elevation-2",
        ],
      },
    },
    defaultVariants: {
      section: "body",
    },
  },
);

const rowAccentVariants = cva(
  [
    "[&>td:first-child]:relative",
    "[&>td:first-child]:before:pointer-events-none",
    "[&>td:first-child]:before:absolute",
    "[&>td:first-child]:before:inset-y-0",
    "[&>td:first-child]:before:left-0",
    "[&>td:first-child]:before:w-(--spacing-4)",
    "[&>td:first-child]:before:content-['']",
  ],
  {
    variants: {
      tone: {
        brand: "[&>td:first-child]:before:bg-primary",
        positive: "[&>td:first-child]:before:bg-positive",
        warning: "[&>td:first-child]:before:bg-accessibility-warning",
        negative: "[&>td:first-child]:before:bg-negative",
        informative: "[&>td:first-child]:before:bg-informative",
      },
    },
  },
);

const alignVariants = cva([], {
  variants: {
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
  },
  defaultVariants: {
    align: "left",
  },
});

const headVariants = cva(
  [
    "h-(--spacing-40)",
    "whitespace-nowrap",
    "border-b",
    "border-table-outline",
    "bg-table-row-alt-surface",
    "py-0",
    "align-middle",
    "font-(family-name:--font-family-label)",
    "text-(length:--font-size-label-small)",
    "font-(number:--font-weight-semibold)",
    "leading-(--font-line-height-16)",
    "text-table-header-text",
  ],
  {
    variants: {
      variant: {
        default: ["px-(--spacing-16)"],
        starting: ["pl-(--spacing-24)", "pr-(--spacing-16)"],
        spacer: ["p-0"],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const cellVariants = cva(
  [
    "h-(--table-row-height)",
    "min-w-0",
    "px-(--spacing-16)",
    "py-0",
    "align-middle",
    "text-text-default",
  ],
  {
    variants: {
      variant: {
        starting: ["pl-(--spacing-24)", "pr-(--spacing-16)"],
        text: null,
        doubleLine: null,
        chip: null,
        chipText: null,
        currency: ["font-(family-name:--font-family-label)"],
        icon: ["px-(--spacing-16)", "text-center"],
        button: null,
        toggle: null,
        dropdown: null,
        input: null,
        calendar: null,
        expand: ["px-(--spacing-16)", "text-center"],
        endingSpacer: ["p-0"],
      },
    },
    defaultVariants: {
      variant: "text",
    },
  },
);

type WidthValue = number | string;

function resolveWidth(width?: WidthValue, variant?: TableCellVariant) {
  if (width !== undefined) {
    return typeof width === "number" ? `${width}px` : width;
  }

  return variant ? DEFAULT_WIDTH_BY_VARIANT[variant] : undefined;
}

function getStyleWithWidth(
  style: React.CSSProperties | undefined,
  width: string | undefined,
): React.CSSProperties | undefined {
  if (!width) return style;

  return {
    ...style,
    width,
    minWidth: width,
  };
}

function getHideBelowClassName(
  hideBelow: TableContainerBreakpoint | undefined,
) {
  if (hideBelow === "compact") return "@max-[480px]/data-table:hidden";
  if (hideBelow === "comfortable") return "@max-[768px]/data-table:hidden";
  if (hideBelow === "expanded") return "@max-[1024px]/data-table:hidden";
  if (hideBelow === "wide") return "@max-[1280px]/data-table:hidden";
  return undefined;
}

export interface TableProps
  extends Omit<React.ComponentPropsWithoutRef<"table">, "className">,
    VariantProps<typeof tableVariants> {
  /** Row height scale. Defaults to "md" (68px body rows). */
  size?: TableSize;
}

function Table({ size = "md", style, ...props }: TableProps) {
  return (
    <table
      data-slot="table"
      data-size={size}
      className={tableVariants({ size })}
      style={style}
      {...props}
    />
  );
}

export type TableHeaderProps = Omit<
  React.ComponentPropsWithoutRef<"thead">,
  "className"
>;

function TableHeader(props: TableHeaderProps) {
  return <thead data-slot="table-header" {...props} />;
}

export type TableBodyProps = Omit<
  React.ComponentPropsWithoutRef<"tbody">,
  "className"
>;

function TableBody(props: TableBodyProps) {
  return <tbody data-slot="table-body" {...props} />;
}

export interface TableRowProps
  extends Omit<React.ComponentPropsWithoutRef<"tr">, "className">,
    VariantProps<typeof rowVariants> {
  accentTone?: TableRowAccentTone;
  selected?: boolean;
  interactive?: boolean;
}

function TableRow({
  accentTone,
  selected = false,
  interactive = false,
  section,
  children,
  ...props
}: TableRowProps) {
  return (
    <tr
      data-slot="table-row"
      data-selected={selected ? "" : undefined}
      data-interactive={interactive ? "" : undefined}
      aria-selected={selected || undefined}
      className={cn(
        rowVariants({ section }),
        section !== "header" &&
          accentTone &&
          rowAccentVariants({ tone: accentTone }),
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export interface TableHeadProps
  extends Omit<React.ComponentPropsWithoutRef<"th">, "align" | "className">,
    VariantProps<typeof alignVariants>,
    VariantProps<typeof headVariants> {
  leadingCheckbox?: React.ReactNode;
  sortable?: boolean;
  sortDirection?: TableSortDirection;
  tooltip?: string;
  hideBelow?: TableContainerBreakpoint;
  width?: WidthValue;
  onSort?: React.MouseEventHandler<HTMLButtonElement>;
}

function getAriaSort(sortDirection: TableSortDirection) {
  if (!sortDirection) return "none";
  return sortDirection;
}

interface SortIndicatorProps {
  direction: TableSortDirection;
}

function getSortIndicatorTone(
  direction: TableSortDirection,
  icon: "ascending" | "descending",
) {
  if (!direction) return "text-icon-main";
  return direction === icon ? "text-primary" : "text-icon-disabled";
}

function SortIndicator({ direction }: SortIndicatorProps) {
  return (
    <span
      aria-hidden="true"
      className="relative block h-[28px] w-(--spacing-20) shrink-0"
    >
      <span
        className={cn(
          "absolute left-(--spacing-2) top-[3px] inline-flex",
          getSortIndicatorTone(direction, "ascending"),
        )}
      >
        <Icon name="chevron-up" size="xs" tone="inherit" />
      </span>
      <span
        className={cn(
          "absolute left-(--spacing-2) top-[9px] inline-flex",
          getSortIndicatorTone(direction, "descending"),
        )}
      >
        <Icon name="chevron-down" size="xs" tone="inherit" />
      </span>
    </span>
  );
}

function TableHead({
  align,
  children,
  leadingCheckbox,
  sortable = false,
  sortDirection = false,
  tooltip,
  hideBelow,
  variant,
  width,
  style,
  onSort,
  ...props
}: TableHeadProps) {
  const resolvedWidth = resolveWidth(width);
  const tooltipTrigger = tooltip ? (
    <Tooltip label={tooltip} showArrow={false}>
      <button
        type="button"
        className="inline-flex shrink-0 items-center border-0 bg-transparent p-0 text-icon-secondary outline-none focus-visible:ring-2 focus-visible:ring-button-focus-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface"
        aria-label={tooltip}
      >
        <Icon name="circle-info" size="xs" tone="inherit" />
      </button>
    </Tooltip>
  ) : null;
  const labelContent = (
    <span
      className={cn(
        "flex min-w-0 items-center gap-(--spacing-8)",
        align === "right" && "justify-end",
        align === "center" && "justify-center",
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
      {sortable && <SortIndicator direction={sortDirection} />}
    </span>
  );
  const content = (
    <span
      className={cn(
        "flex min-w-0 items-center gap-(--spacing-12)",
        align === "right" && "justify-end",
        align === "center" && "justify-center",
      )}
    >
      {leadingCheckbox && (
        <span className="inline-flex shrink-0">{leadingCheckbox}</span>
      )}
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            "flex h-full min-w-0 items-center bg-transparent p-0 text-inherit",
            "border-0 outline-none cursor-pointer",
            "focus-visible:ring-2 focus-visible:ring-button-focus-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
            align === "right" && "justify-end",
            align === "center" && "justify-center",
          )}
        >
          {labelContent}
        </button>
      ) : (
        labelContent
      )}
      {tooltipTrigger}
    </span>
  );

  return (
    <th
      data-slot="table-head"
      aria-sort={sortable ? getAriaSort(sortDirection) : undefined}
      className={cn(
        headVariants({ variant }),
        alignVariants({ align }),
        getHideBelowClassName(hideBelow),
      )}
      style={getStyleWithWidth(style, resolvedWidth)}
      {...props}
    >
      {content}
    </th>
  );
}

export interface TableCellProps
  extends Omit<React.ComponentPropsWithoutRef<"td">, "align" | "className">,
    VariantProps<typeof alignVariants>,
    VariantProps<typeof cellVariants> {
  hideBelow?: TableContainerBreakpoint;
  width?: WidthValue;
  emptyFallback?: React.ReactNode;
}

function TableCellRoot({
  align,
  variant,
  hideBelow,
  width,
  style,
  children,
  emptyFallback = "--",
  ...props
}: TableCellProps) {
  const resolvedVariant = variant ?? "text";
  const resolvedWidth = resolveWidth(width, resolvedVariant);
  const isEmpty =
    children === null ||
    children === undefined ||
    (typeof children === "string" && children.trim() === "");

  return (
    <td
      data-slot="table-cell"
      data-variant={resolvedVariant}
      className={cn(
        cellVariants({ variant: resolvedVariant }),
        alignVariants({ align }),
        getHideBelowClassName(hideBelow),
      )}
      style={getStyleWithWidth(style, resolvedWidth)}
      {...props}
    >
      {resolvedVariant === "endingSpacer"
        ? null
        : isEmpty
          ? emptyFallback
          : children}
    </td>
  );
}

export interface TableCellStackProps {
  children: React.ReactNode;
}

function TableCellStack({ children }: TableCellStackProps) {
  return (
    <div className="flex min-w-0 flex-col gap-(--spacing-2)">{children}</div>
  );
}

export interface TableCellTextProps {
  children: React.ReactNode;
}

function TableCellPrimary({ children }: TableCellTextProps) {
  return (
    <span className="min-w-0 truncate font-(family-name:--font-family-body) text-(length:--font-size-body-medium) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default">
      {children}
    </span>
  );
}

function TableCellSecondary({ children }: TableCellTextProps) {
  return (
    <span className="min-w-0 truncate font-(family-name:--font-family-body) text-(length:--font-size-body-small) font-(number:--font-weight-regular) leading-(--font-line-height-16) text-text-subtext">
      {children}
    </span>
  );
}

export interface TableStartingCellContentProps {
  status?: React.ReactNode;
  checkbox?: React.ReactNode;
  thumbnail?: React.ReactNode;
  avatar?: React.ReactNode;
  icon?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  chip?: React.ReactNode;
}

function TableStartingCellContent({
  status,
  checkbox,
  thumbnail,
  avatar,
  icon,
  primary,
  secondary,
  chip,
}: TableStartingCellContentProps) {
  return (
    <div className="flex min-w-0 items-center gap-(--spacing-8)">
      {status}
      {checkbox}
      {thumbnail}
      {avatar}
      {icon}
      <div className="flex min-w-0 flex-col justify-center">
        <TableCellPrimary>{primary}</TableCellPrimary>
        {(secondary || chip) && (
          <div className="flex min-w-0 flex-wrap items-center gap-(--spacing-4) pt-(--spacing-4)">
            {secondary && <TableCellSecondary>{secondary}</TableCellSecondary>}
            {chip && <span className="shrink-0">{chip}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export interface TableTextCellContentProps {
  leadingIcon?: React.ReactNode;
  main: React.ReactNode;
  supporting?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

function TableTextCellContent({
  leadingIcon,
  main,
  supporting,
  trailingIcon,
}: TableTextCellContentProps) {
  return (
    <div className="flex min-w-0 items-center gap-(--spacing-8)">
      {leadingIcon && <span className="shrink-0">{leadingIcon}</span>}
      <TableCellStack>
        <TableCellPrimary>{main}</TableCellPrimary>
        {supporting && <TableCellSecondary>{supporting}</TableCellSecondary>}
      </TableCellStack>
      {trailingIcon && <span className="shrink-0">{trailingIcon}</span>}
    </div>
  );
}

export interface TableChipTextCellContentProps {
  chip: React.ReactNode;
  secondary?: React.ReactNode;
}

function TableChipTextCellContent({
  chip,
  secondary,
}: TableChipTextCellContentProps) {
  return (
    <div className="flex min-w-0 items-center gap-(--spacing-8)">
      <span className="shrink-0">{chip}</span>
      {secondary && (
        <span className="min-w-0 truncate text-text-subtext">{secondary}</span>
      )}
    </div>
  );
}

const TableCell = Object.assign(TableCellRoot, {
  ChipText: TableChipTextCellContent,
  Primary: TableCellPrimary,
  Secondary: TableCellSecondary,
  Stack: TableCellStack,
  Starting: TableStartingCellContent,
  Text: TableTextCellContent,
});

export {
  Table,
  TableBody,
  TableCell,
  TableChipTextCellContent,
  TableCellPrimary,
  TableCellSecondary,
  TableCellStack,
  TableHead,
  TableHeader,
  TableRow,
  TableStartingCellContent,
  TableTextCellContent,
};
